import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import DashboardLayout from '../components/layout/DashboardLayout';
import DataTable from '../components/common/DataTable';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import usePageTitle from '../hooks/usePageTitle';
import useSmartRefresh from '../hooks/useSmartRefresh';

/**
 * Orders Page - Modern table/grid layout for order management
 */
const OrdersPage = () => {
  // Set page title
  usePageTitle('Orders');

  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({
    total_count: 0,
    total_revenue: 0,
    completed_count: 0,
    pending_count: 0,
  });

  // Fetch orders on mount
  useEffect(() => {
    fetchOrdersData();
  }, []);

  // Fetch orders data
  const fetchOrdersData = async () => {
    try {
      const { default: imeiService } = await import('../services/imeiService');

      // Fetch history and stats
      const [historyResponse, statsResponse] = await Promise.all([
        imeiService.getHistory(),
        imeiService.getStats(),
      ]);

      // Handle history data
      const historyData = historyResponse.results || historyResponse;
      if (Array.isArray(historyData)) {
        setOrders(historyData);
      }

      // Handle stats data
      if (statsResponse) {
        setStats({
          total_count:
            statsResponse.total ||
            statsResponse.total_orders ||
            statsResponse.total_count ||
            0,
          total_revenue:
            statsResponse.revenue ||
            statsResponse.total_revenue ||
            statsResponse.total_spent ||
            0,
          completed_count:
            statsResponse.completed ||
            statsResponse.completed_orders ||
            statsResponse.completed_count ||
            0,
          pending_count:
            statsResponse.pending ||
            statsResponse.pending_orders ||
            statsResponse.pending_count ||
            0,
        });
      }
    } catch (err) {
      console.error('Error fetching orders:', err);
    }
  };

  const { isRefreshing, refreshData } = useSmartRefresh(
    fetchOrdersData,
    'Orders refreshed successfully',
  );

  const [selectedOrder, setSelectedOrder] = useState(null);

  // Helper to format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Helper to format time
  const formatTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Helper to get device info from result
  const getDeviceInfo = (order) => {
    if (order.result && typeof order.result === 'object') {
      const brand = order.result.brand || order.result.Brand || '';
      const model = order.result.model || order.result.Model || '';
      if (brand || model) return `${brand} ${model}`.trim();
    }
    return order.device_name || 'Unknown Device';
  };

  // Define table columns with equal distribution
  const columns = [
    {
      id: 'id',
      label: 'Order ID',
      sortable: true,
      width: '16%',
      render: (value, item) => (
        <div>
          <div className="flex items-center space-x-2">
            <div className="font-medium text-gray-900 dark:text-gray-100">#{value}</div>
            {item.batch ? (
              <span className="px-1.5 py-0.5 text-[10px] font-bold bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 rounded uppercase">
                Bulk
              </span>
            ) : (
              <span className="px-1.5 py-0.5 text-[10px] font-bold bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 rounded uppercase">
                Single
              </span>
            )}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 font-mono">{item.imei}</div>
        </div>
      ),
    },
    {
      id: 'user_email',
      label: 'Customer',
      sortable: true,
      width: '18%',
      render: (value) => (
        <div className="flex items-center">
          <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mr-3">
            <span className="text-primary-600 dark:text-primary-400 font-medium text-sm">
              {value ? value[0].toUpperCase() : 'U'}
            </span>
          </div>
          <span
            className="font-medium text-gray-900 dark:text-gray-100 truncate max-w-[120px]"
            title={value}
          >
            {value ? value.split('@')[0] : 'User'}
          </span>
        </div>
      ),
    },
    {
      id: 'result',
      label: 'Device',
      sortable: false,
      width: '18%',
      render: (value, item) => (
        <div className="flex items-center">
          <div className="w-8 h-8 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center mr-3">
            <svg
              className="w-4 h-4 text-gray-600 dark:text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3"
              />
            </svg>
          </div>
          <span className="text-gray-900 dark:text-gray-100 truncate max-w-[120px]">
            {getDeviceInfo(item)}
          </span>
        </div>
      ),
    },
    {
      id: 'service_name',
      label: 'Service',
      sortable: true,
      width: '14%',
    },
    {
      id: 'cost',
      label: 'Amount',
      sortable: true,
      align: 'center',
      width: '10%',
      render: (value) => (
        <span className="font-semibold text-gray-900 dark:text-gray-100">
          ${parseFloat(value || 0).toFixed(2)}
        </span>
      ),
    },
    {
      id: 'status',
      label: 'Status',
      sortable: true,
      align: 'center',
      width: '12%',
      render: (value) => {
        const isCompleted = value === 'completed' || value === 'Success';
        const isProcessing = value === 'processing';
        const isFailed = value === 'failed' || value === 'Failed';

        const config = isCompleted
          ? {
              bg: 'bg-success-100 dark:bg-success-900/30',
              text: 'text-success-700 dark:text-success-400',
              label: 'Success',
              icon: (
                <svg
                  className="w-3 h-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              ),
            }
          : isProcessing
            ? {
                bg: 'bg-primary-100 dark:bg-primary-900/30',
                text: 'text-primary-700 dark:text-primary-400',
                label: 'Processing',
                icon: (
                  <svg
                    className="w-3 h-3 animate-spin"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                ),
              }
            : isFailed
              ? {
                  bg: 'bg-error-100 dark:bg-error-900/30',
                  text: 'text-error-700 dark:text-error-400',
                  label: 'Failed',
                  icon: (
                    <svg
                      className="w-3 h-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  ),
                }
              : {
                  bg: 'bg-warning-100 dark:bg-warning-900/30',
                  text: 'text-warning-700 dark:text-warning-400',
                  label: value || 'Pending',
                  icon: (
                    <svg
                      className="w-3 h-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  ),
                };

        return (
          <span
            className={`inline-flex items-center space-x-1 px-2.5 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}
          >
            {config.icon}
            <span className="capitalize">{config.label}</span>
          </span>
        );
      },
    },
    {
      id: 'created_at',
      label: 'Date',
      sortable: true,
      width: '12%',
      render: (value) => (
        <div>
          <div className="text-gray-900 dark:text-gray-100">
            {formatDate(value)}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {formatTime(value)}
          </div>
        </div>
      ),
    },
  ];

  // Define table actions
  const actions = [
    {
      type: 'view',
      icon: (
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
          />
        </svg>
      ),
      tooltip: 'View Details',
      variant: 'primary',
    },
    {
      type: 'download',
      icon: (
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
      ),
      tooltip: 'Download Receipt',
    },
  ];

  // Define filters
  const filters = [
    {
      key: 'status',
      label: 'Status',
      options: [
        { value: 'completed', label: 'Completed' },
        { value: 'processing', label: 'Processing' },
        { value: 'pending', label: 'Pending' },
        { value: 'failed', label: 'Failed' },
      ],
    },
    {
      key: 'service',
      label: 'Service',
      options: [
        { value: 'IMEI Check', label: 'IMEI Check' },
        { value: 'Network Unlock', label: 'Network Unlock' },
        { value: 'Carrier Check', label: 'Carrier Check' },
      ],
    },
  ];

  // Action handlers
  const handleView = (order) => {
    setSelectedOrder(order);
  };

  const handleDownload = (order) => {
    handleDownloadReceipt(order);
  };

  const handleDownloadReceipt = (order) => {
    const doc = new jsPDF();

    // Header
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('DeviceLink Global Pvt. Ltd.', 20, 20);

    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text('Professional IMEI Services', 20, 30);
    doc.text('Order Receipt', 20, 45);

    // Order Details
    doc.setFont('helvetica', 'bold');
    doc.text('Order Details:', 20, 65);
    doc.setFont('helvetica', 'normal');
    doc.text(`Order Number: #${order.id}`, 20, 75);
    doc.text(`Date: ${formatDate(order.created_at)}`, 20, 85);
    doc.text(`Time: ${formatTime(order.created_at)}`, 20, 95);
    doc.text(
      `Status: ${order.status.charAt(0).toUpperCase() + order.status.slice(1)}`,
      20,
      105,
    );

    // Service Details
    doc.setFont('helvetica', 'bold');
    doc.text('Service Details:', 20, 125);
    doc.setFont('helvetica', 'normal');
    doc.text(`Device: ${getDeviceInfo(order)}`, 20, 135);
    doc.text(`IMEI: ${order.imei}`, 20, 145);
    doc.text(`Service: ${order.service_name || 'IMEI Check'}`, 20, 155);
    doc.text(`Amount: $${parseFloat(order.cost || 0).toFixed(2)}`, 20, 165);

    // Footer
    doc.setFontSize(10);
    doc.setFont('helvetica', 'italic');
    doc.text('Thank you for your order!', 20, 190);
    doc.text(
      'This document was generated electronically by DeviceLink Global Pvt. Ltd.',
      20,
      200,
    );

    // Save
    doc.save(`order-${order.id}.pdf`);
  };

  return (
    <DashboardLayout pageName="Orders">
      <div className="space-y-8">
        {/* Page Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-gray-500 dark:text-gray-400 text-sm">
            Track and manage your IMEI service orders with advanced filtering
            and search
          </p>
        </motion.div>

        {/* Summary Cards */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-xl flex items-center justify-center mr-4">
                <svg
                  className="w-6 h-6 text-primary-600 dark:text-primary-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h4.125M8.25 8.25l13.5 0"
                  />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Total Orders
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {stats.total_count}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  All time orders
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-success-100 dark:bg-success-900/30 rounded-xl flex items-center justify-center mr-4">
                <svg
                  className="w-6 h-6 text-success-600 dark:text-success-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Total Revenue
                </p>
                <p className="text-2xl font-bold text-success-600 dark:text-success-400">
                  ${parseFloat(stats.total_revenue || 0).toFixed(2)}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {stats.completed_count} completed
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-warning-100 dark:bg-warning-900/30 rounded-xl flex items-center justify-center mr-4">
                <svg
                  className="w-6 h-6 text-warning-600 dark:text-warning-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Pending Orders
                </p>
                <p className="text-2xl font-bold text-warning-600 dark:text-warning-400">
                  {stats.pending_count}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Awaiting processing
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* DataTable Component */}
        <DataTable
          title="Order Management"
          columns={columns}
          data={orders}
          actions={actions}
          onView={handleView}
          onDownload={handleDownload}
          onRefresh={refreshData}
          searchPlaceholder="Search orders, customers, devices..."
          filters={filters}
          rowsPerPageOptions={[5, 10, 20, 50]}
          defaultRowsPerPage={10}
          exportEnabled={true}
          refreshEnabled={true}
          isLoading={isRefreshing}
          className="mt-6"
        />
      </div>

      {/* Order View Modal */}
      <AnimatePresence>
        {selectedOrder && (
          <motion.div
            className="fixed inset-0 z-50 overflow-y-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <motion.div
              className="fixed inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-sm"
              onClick={() => setSelectedOrder(null)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />

            <div className="flex items-center justify-center min-h-screen px-4 py-8">
              <motion.div
                className="relative w-full max-w-3xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-2xl rounded-2xl overflow-hidden transition-colors duration-300"
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
              >
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                  <div>
                    <h2 className="text-2xl font-semibold text-sky-600 dark:text-sky-400">
                      Order Details
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {selectedOrder.id}
                    </p>
                  </div>
                  <button
                    onClick={() => setSelectedOrder(null)}
                    className="p-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Order Info */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                        Order Information
                      </h3>
                      <div className="space-y-3">
                        <div>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                            Order Number
                          </p>
                          <p className="text-sm text-gray-900 dark:text-gray-100">
                            #{selectedOrder.id}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                            Date & Time
                          </p>
                          <p className="text-sm text-gray-900 dark:text-gray-100">
                            {formatDate(selectedOrder.created_at)} at{' '}
                            {formatTime(selectedOrder.created_at)}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                            Status
                          </p>
                          <span
                            className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                              selectedOrder.status === 'completed'
                                ? 'bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400'
                                : selectedOrder.status === 'pending'
                                  ? 'bg-warning-100 text-warning-700 dark:bg-warning-900/30 dark:text-warning-400'
                                  : selectedOrder.status === 'failed'
                                    ? 'bg-error-100 text-error-700 dark:bg-error-900/30 dark:text-error-400'
                                    : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
                            }`}
                          >
                            {selectedOrder.status.charAt(0).toUpperCase() +
                              selectedOrder.status.slice(1)}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                            Amount
                          </p>
                          <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                            ${parseFloat(selectedOrder.cost || 0).toFixed(2)}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Device Details */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                        Device Details
                      </h3>
                      <div className="space-y-3">
                        <div>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                            Device
                          </p>
                          <p className="text-sm text-gray-900 dark:text-gray-100">
                            {getDeviceInfo(selectedOrder)}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                            IMEI Number
                          </p>
                          <p className="text-sm text-gray-900 dark:text-gray-100 font-mono">
                            {selectedOrder.imei}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                            Service
                          </p>
                          <p className="text-sm text-gray-900 dark:text-gray-100">
                            {selectedOrder.service_name || 'IMEI Check'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Order Timeline */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                      Order Progress
                    </h3>
                    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                      <div className="flex items-center space-x-4">
                        <div
                          className={`w-3 h-3 rounded-full ${
                            selectedOrder.status === 'completed'
                              ? 'bg-success-500'
                              : selectedOrder.status === 'pending'
                                ? 'bg-warning-500'
                                : selectedOrder.status === 'failed'
                                  ? 'bg-error-500'
                                  : 'bg-gray-400'
                          }`}
                        ></div>
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {selectedOrder.status === 'completed'
                              ? 'Order completed successfully'
                              : selectedOrder.status === 'pending'
                                ? 'Order is being processed'
                                : selectedOrder.status === 'failed'
                                  ? 'Order processing failed'
                                  : 'Order status unknown'}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {selectedOrder.status === 'completed'
                              ? 'Your IMEI check results are ready'
                              : selectedOrder.status === 'pending'
                                ? 'Please wait while we process your request'
                                : selectedOrder.status === 'failed'
                                  ? 'Please contact support for assistance'
                                  : 'Contact support for more information'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end p-6 border-t border-gray-200 dark:border-gray-700 space-x-3">
                  <button
                    onClick={() => handleDownloadReceipt(selectedOrder)}
                    className="px-4 py-2 bg-gradient-to-r from-sky-500 to-blue-600 text-white rounded-lg font-medium hover:from-sky-600 hover:to-blue-700 transition-all duration-300"
                  >
                    Download Receipt
                  </button>
                  <button
                    onClick={() => setSelectedOrder(null)}
                    className="px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200"
                  >
                    Close
                  </button>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </DashboardLayout>
  );
};

export default OrdersPage;
